<script>
import { preventDefault, stopPropagation } from "svelte/legacy";
import { onMount, tick } from "svelte";
import { eventGroupState, isSelectedEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { playerState } from "@app/store/playerStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { lockState } from "@app/store/lockStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { editorState, filterState } from "@app/store/editorStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import * as m from '@app/paraglide/messages.js';
import { Circle } from "svelte-loading-spinners";
import { decode } from "html-entities";
import QuillEditor from "./QuillEditor.svelte";
import TranscriptEditorPreview from "./TranscriptEditorPreview.svelte";
import OriginalTextPreview from "./OriginalTextPreview.svelte";
import TcInput from "./inputs/TcInput.svelte";
import SliderInput from "./inputs/SliderInput.svelte";
import RealTimeErrors from "./RealTimeErrors.svelte";
import EventNotes from "./EventNotes.svelte";
import EventApproval from "./EventApproval.svelte";
import ForcedSubtitleEvent from "./ForcedSubtitleEvent.svelte";
import EventTrackSelect from "./EventTrackSelect.svelte";
import EventMetrics from "./EventMetrics.svelte";
import EventSpeakers from "./EventSpeakers.svelte";
import EventRecipients from "./EventRecipients.svelte";
import EventTags from "./EventTags.svelte";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
import sccLib from "@cclib/dict/608.js";
import insertEv from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import { clickoutside } from "@svelte-put/clickoutside";

// Import the shared utility functions from styleGuideTests.js
import {
    getTotalChars,    
    calculateCps,
    calculateWpm,
    calculateDuration,
} from "@cclib/lib/styleGuideTests.js";
import { getFilteredEventIndices } from "@app/lib/eventValidation.js";
import {
    applyEventClick,
    getSelectionAnchor,
    setSelectionAnchor,
} from "@app/lib/eventSelection.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import { assignEventAudio } from "@app/lib/audio/assignEventAudio.js";
import { eventNeedsRender } from "@app/lib/audio/audioRenderState.js";
import {
    refreshEventAudioUrl,
    isSignedUrlExpired,
} from "@app/lib/audio/refreshEventAudioUrl.js";
import {
    WEB_SPEECH_PROVIDER,
    cancel as cancelWebSpeech,
    speak as speakWebSpeech,
} from "@app/lib/audio/webSpeechTts.js";
import { renderEventTts, getEventTtsText } from "@app/lib/audio/renderEventAudio.js";
import {
    canFitEvent,
    computeResidualSpeed,
} from "@app/lib/audio/fitEventToDuration.js";
import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
import { AUDIO_DUBBING } from "@app/lib/eventGroupTypes.js";

/* Firebase */
import { functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";

/** @type {{eventIndex: any, compact?: boolean}} */
let { eventIndex, compact = false } = $props();
let event = $derived(eventGroupState[projectState.selected]?.events[eventIndex]);
let isLocked = $derived(isSelectedEventGroupLocked());
let isSelected = $derived(eventGroupState[projectState.selected]?.selected[0] === eventIndex);
let isDubbing = $derived(eventGroupState[projectState.selected]?.type === AUDIO_DUBBING);
let allowOriginalEdit = $derived(!!eventGroupState[projectState.selected]?.allowOriginalEdit);
let rendering = $state(false);
let showAiTools = $state(false);
let aiToolsBtnEl = $state(null);
let aiMenuPos = $state({ top: 0, left: 0 });
let previewAudioPlayer;
let previewAudioPlaying = $state(false);
let webSpeechPreviewStopRequested = false;

const audioPreviewStartedEvent = "audioDescriptionPreviewAudioStarted";

/* Emotion tags like [laughs] are delivery cues, not spoken words, so metrics ignore them */
let plainText = $derived(voiceUtils.stripEmotionTags(convertToPlainText(event?.text  || "")));
let emotionTagsSupported = $derived(voiceUtils.supportsEmotionTags(event?.voice, event?.speakingStyle));
let audibleDuration = $derived(voiceUtils.effectiveAudioDuration(event));
let isWebSpeechVoice = $derived(event?.voice?.provider === WEB_SPEECH_PROVIDER);
let isElevenLabsVoice = $derived(event?.voice?.provider === "ElevenLabs");
let elevenLabsModel = $derived(isElevenLabsVoice ? voiceUtils.resolveElevenLabsModel(event.voice, event.speakingStyle) : null);
let canPreviewAudio = $derived(isWebSpeechVoice ? plainText.trim().length > 0 : !!event?.audioFile);
let needsRender = $derived(eventNeedsRender(event));

let lineInfo = $derived.by(() => {
    let lines = [];
    plainText.split("\n").forEach((line) => {
        lines.push(decode(line).length);
    });

    return lines;
});

let totalWords = $derived(plainText.split(/\s/gm).length);

let totalChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Use the imported getTotalChars function
    return getTotalChars(plainText, "text");
});

let cpsChars = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return 0;
    // Reading rate (CPS) can optionally exclude spaces based on user metrics settings
    return getTotalChars(plainText, "text", metricsState.cpsIncludeSpaces === false);
});

let duration = $derived(calculateDuration(event.start, event.end, 3));
let wpm = $derived(calculateWpm(totalWords, duration));
let cps = $derived(calculateCps(cpsChars, duration));

let overlaps = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    if (event.start === false || event.end === false) return null;
    if (event.start < eventGroupState[projectState.selected]?.events[eventIndex-1]?.end || event.end > eventGroupState[projectState.selected]?.events[eventIndex+1]?.start) {
        return true;
    }

    return false;    
});

let illegalChars = $derived.by(() => {
    return plainText.split("").find((char) => {
        return sccLib.valid.indexOf(char) === -1;
    });
});

let errorMsg = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return null;
    let messages = [];

    if (overlaps && eventGroupState[projectState.selected].overlap) {
        messages.push("• Event overlap detected.");
    }

    if (eventGroupState[projectState.selected].illegalChars && illegalChars) {
        messages.push(
            `• Illegal non-608 compliant character found ${illegalChars}`,
        );
    }

    if (
        lineInfo.some((line) => {
            return line > eventGroupState[projectState.selected]?.maxChars;
        })
    ) {
        messages.push(
            `• Event exceeds maximum character count of ${eventGroupState[projectState.selected]?.maxChars}.`,
        );
    }

    if (lineInfo.length > eventGroupState[projectState.selected]?.maxLines) {
        messages.push(
            `• Event exceeds maximum line count of ${eventGroupState[projectState.selected]?.maxLines}.`,
        );
    }

    if (event.start !== false && event.end !== false){
        if (duration < eventGroupState[projectState.selected]?.minDuration){
            messages.push(
                `• Event is less than the minimum duration of ${eventGroupState[projectState.selected]?.minDuration} seconds.`,
            );
        } else if (duration > eventGroupState[projectState.selected]?.maxDuration){
            messages.push(
                `• Event exceeds maximum duration of ${eventGroupState[projectState.selected]?.maxDuration} seconds.`,
            );
        } else if (cps > eventGroupState[projectState.selected]?.maxCps){
            messages.push(
                `• Event exceeds maximum CPS (${eventGroupState[projectState.selected]?.maxCps}).`,
            );
        } else if (wpm > eventGroupState[projectState.selected]?.maxWpm){
            messages.push(
                `• Event exceeds maximum WPM (${eventGroupState[projectState.selected]?.maxWpm}).`,
            );
        }
    }

    return messages.join("\n");
});

onMount(() => {
    function stopOtherPreviewAudio(previewEvent) {
        if (previewEvent.detail?.eventIndex !== eventIndex) {
            stopPreviewAudio();
        }
    }

    window.addEventListener(audioPreviewStartedEvent, stopOtherPreviewAudio);

    return () => {
        stopPreviewAudio();
        window.removeEventListener(audioPreviewStartedEvent, stopOtherPreviewAudio);
    };
});

function selectEvent(clickEvent, index) {
    try {
        const eventGroup = eventGroupState[projectState.selected];
        if (!eventGroup) return;

        const isToggle = clickEvent.ctrlKey || clickEvent.metaKey;
        const isRange =
            clickEvent.shiftKey && (eventGroup.selected?.length ?? 0) > 0;

        const { selected, anchor } = applyEventClick({
            eventIndex: index,
            modifiers: clickEvent,
            currentSelected: eventGroup.selected,
            filteredIndices: getFilteredEventIndices(
                eventGroup.events,
                eventGroup,
                filterState,
            ),
            anchor: getSelectionAnchor(eventGroup.id, eventGroup.selected?.[0]),
        });
        eventGroup.selected = selected;
        setSelectionAnchor(eventGroup.id, anchor);

        if (!isToggle && !isRange) {
            if (
                lockState.video &&
                eventGroup.events[index].start &&
                !isNaN(eventGroup.events[index].start)
            ) {
                player.currentTime = eventGroup.events[index].start;
            }

            if (eventGroup.selected[0] >= 0) {
                window.dispatchEvent(
                    new CustomEvent("scrollToVirtualListItem", {
                        detail: { index: index },
                    }),
                );
            }
        }
    } catch (err) {
        console.log("There was an error selecting the event. " + err.message);
        console.log(err);
    }
}

function insertEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        let eventOptions;
        if (eventGroupState[projectState.selected].events.length > 0) {
            try {
                let eventTemplate =
                    eventGroupState[projectState.selected].selected.length > 0
                        ? eventGroupState[projectState.selected].events[
                              eventGroupState[projectState.selected].selected[0]
                          ]
                        : eventGroupState[projectState.selected].events[
                              eventGroupState[projectState.selected].events
                                  .length - 1
                          ];

                eventOptions = {
                    alignment: eventTemplate.alignment,
                    xPos: eventTemplate.xPos,
                    yPos: eventTemplate.yPos,
                    vertical: eventTemplate.vertical,
                    voice: eventTemplate.voice,
                    speakingStyle: eventTemplate.speakingStyle,
                    style: eventTemplate.style,
                    rate: eventTemplate.rate,
                    speed: eventTemplate.speed,
                    track: eventTemplate.track ?? false,
                    start: editorState.insertStartOnInsert
                        ? player.currentTime
                        : false,
                    end: editorState.insertStartOnInsert
                        ? player.currentTime +
                          eventGroupState[projectState.selected].minDuration
                        : false,
                };
            } catch (err) {
                console.log(err);
            }
        }

        // Insert new event
        eventGroupState[projectState.selected] = insertEv(
            eventGroupState[projectState.selected],
            eventOptions,
            eventIndex + 1,
        );        setTimeout(function () {
            eventGroupState[projectState.selected].selected = [eventIndex + 1];
            const selectedIndex = eventIndex + 1;

            // Dispatch custom event to scroll to the newly inserted event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: selectedIndex }
            }));
        }, 20);

        /* Store in History */
        insertHistoryAction({
            name: "insert event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

async function deleteEvent() {
    try {
        if (!eventGroupState[projectState.selected]) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        // Remove the event
        const eventIndexToDelete =  eventIndex;      
        // Update selection to previous event or clear if no events left
        let newSelectedIndex = eventIndexToDelete > 0 ? eventIndexToDelete - 1 : 0;
        if (!eventGroupState[projectState.selected].events[newSelectedIndex]) {
            eventGroupState[projectState.selected].selected = [];
        } else {
            eventGroupState[projectState.selected].selected = [newSelectedIndex];
            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: newSelectedIndex }
            }));
        }

        await tick();

        eventGroupState[projectState.selected] = removeEvent(eventGroupState[projectState.selected], eventIndexToDelete);

        /* Store in History */
        insertHistoryAction({
            name: "delete event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

async function trimToDuration() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    event.end = event.start + audibleDuration + 0.5;
}

async function startFilePreview() {
    if (isSignedUrlExpired(event.audioFile)) {
        await refreshEventAudioUrl(event);
    }
    previewAudioPlayer.src = event.audioFile;
    previewAudioPlayer.currentTime = 0;
    previewAudioPlayer.load();
    // load() resets playbackRate to defaultPlaybackRate, so set both.
    previewAudioPlayer.preservesPitch = true;
    previewAudioPlayer.defaultPlaybackRate = voiceUtils.clampSpeed(event.speed);
    previewAudioPlayer.playbackRate = voiceUtils.clampSpeed(event.speed);
    await previewAudioPlayer.play();
}

async function previewAudio() {
    if (previewAudioPlaying) {
        stopPreviewAudio();
        return;
    }

    if (!canPreviewAudio || (!isWebSpeechVoice && !previewAudioPlayer)) {
        return;
    }

    try {
        window.dispatchEvent(
            new CustomEvent(audioPreviewStartedEvent, {
                detail: { eventIndex },
            }),
        );

        // Select this event so overlays/scroll track it.
        eventGroupState[projectState.selected].selected = [eventIndex];

        // The AD synth voice plays from this event's independent audio source,
        // so AdPreview must not double-play the same event's audio. Set this
        // before seeking so the player's "seeked" handler doesn't cancel it.
        playerState.previewPlaying = true;

        // Seek the video to the event start so it plays in sync with the voice.
        // Always drive the video, independent of the video-lock toggle.
        try {
            if (
                typeof player !== "undefined" &&
                player &&
                event.start !== false &&
                !isNaN(event.start)
            ) {
                player.currentTime = event.start;
            }
        } catch (err) {
            console.log(err.message);
        }

        if (isWebSpeechVoice) {
            webSpeechPreviewStopRequested = false;
            previewAudioPlaying = true;
            playVideoInSync();
            await speakWebSpeech(plainText, {
                voiceURI: event.voice.voiceURI,
                rate: voiceUtils.clampSpeed(event.speed),
                pitch: parseFloat(event.pitch ?? 1),
                volume: 1,
            });
            stopPreviewAudio();
            return;
        }

        try {
            await startFilePreview();
        } catch (playErr) {
            const refreshed = await refreshEventAudioUrl(event);
            if (!refreshed) throw playErr;
            await startFilePreview();
        }
        previewAudioPlaying = true;
        playVideoInSync();
    } catch (err) {
        previewAudioPlaying = false;
        playerState.previewPlaying = false;
        console.log(err.message);
        if (!webSpeechPreviewStopRequested) {
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        }
    }
}

function playVideoInSync() {
    try {
        if (typeof player !== "undefined" && player?.play) {
            Promise.resolve(player.play()).catch(() => {});
        }
    } catch (err) {
        console.log(err.message);
    }
}

function stopPreviewAudio() {
    const wasPlaying = previewAudioPlaying;
    webSpeechPreviewStopRequested = true;
    cancelWebSpeech();

    if (previewAudioPlayer) {
        previewAudioPlayer.pause();
        previewAudioPlayer.currentTime = 0;
        previewAudioPlayer.removeAttribute("src");
    }

    previewAudioPlaying = false;

    // Only tear down shared video/preview state when THIS instance owned the
    // active preview, so the "stop other previews" listener on idle instances
    // does not pause the video or clear the suppression flag.
    if (wasPlaying) {
        playerState.previewPlaying = false;
        try {
            if (typeof player !== "undefined" && player?.pause) {
                player.pause();
            }
        } catch (err) {
            console.log(err.message);
        }
    }
}

function handlePreviewAudioEnded() {
    stopPreviewAudio();
}

function handlePreviewAudioError() {
    previewAudioPlaying = false;
    toast.push("Unable to preview audio.", {
        classes: ["toast-danger"],
    });
}

function attachPreviewAudioPlayer(node) {
    previewAudioPlayer = node;

    return () => {
        if (previewAudioPlayer === node) {
            previewAudioPlayer = undefined;
        }
    };
}

function updateAiMenuPosition() {
    if (!aiToolsBtnEl) return;
    const rect = aiToolsBtnEl.getBoundingClientRect();
    aiMenuPos = { top: rect.bottom + 2, left: rect.left };
}

function toggleAiToolsMenu() {
    setTimeout(() => {
        showAiTools = !showAiTools;
        if (showAiTools) {
            updateAiMenuPosition();
        }
    }, 0);
}

function closeAiToolsMenu() {
    if (showAiTools) {
        showAiTools = false;
    }
}

/**
 * Svelte 5 attachment that teleports a node to document.body, and also
 * closes the AI tools menu on any scroll (virtual list scroller included)
 * so the menu doesn't float in a stale position above unrelated rows.
 */
function portalAiMenu(node) {
    document.body.appendChild(node);
    const onScroll = () => closeAiToolsMenu();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);

    return () => {
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
        node.remove();
    };
}

async function aiRephrase(rate) {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            closeAiToolsMenu();
            return;
        }

        rendering = true;
        closeAiToolsMenu();

        let text = convertToPlainText(
            event.text,
            " ",
        );
        let duration, wpm;

        if (!text) {
            return;
        }

        if (
            !isNaN(
                event.end,
            ) &&
            !isNaN(
                event.start,
            )
        ) {
            duration =
                event.end -
                event.start;
            wpm = text.split(" ").length / (duration / 60);
        }

        /* console.log("Duration of event: "+ duration);
        console.log("WPM: "+ wpm);
        console.log("Event Text: "+ text); */

        let res = await httpsCallable(functions, "v8AdScriptAssistant")({
            text: text,
            rate: rate,
            wpm: wpm,
            numberOfWords: text.split(" ").length,
            duration: duration,
            groupId: eventGroupState[projectState.selected].id,
            eventId:
                event.id,
        });

        // console.log(res.data);

        event.text =
            convertToHtml(res.data.text);

        try {
            player.pause();
        } catch (err) {
            console.log(err.message);
        }

        eventGroupState[projectState.selected].selected = [];

        setTimeout(() => {
            console.log("Setting rendered to false in AI Rephrase");
            event.rendered = false;
        }, 50);

        /* Store in History */
        insertHistoryAction({
            name: "rephrase text", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.audio_description_rephrase_failed_toast({ message: err.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        rendering = false;
    }
}

function renderAudio() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    eventGroupState[projectState.selected].selected = [eventIndex];
    showModal("renderAudio");
}

async function renderSingleEvent(evt, { autoTrim = editorState.autoTrimEvent } = {}) {
    if (evt?.voice?.provider === WEB_SPEECH_PROVIDER) return;

    const result = await renderEventTts(evt, eventGroupState[projectState.selected].id);
    if (!result) return;

    await assignEventAudio(evt, {
        audioFile: result.audio,
        audioFileDuration: result.durationSec,
        audioFileRemote: result.storagePath,
    });

    if (autoTrim && !isNaN(evt.start) && result.durationSec > 0) {
        evt.end = evt.start + voiceUtils.effectiveAudioDuration(evt) + 0.5;
    }
}

async function autoRate() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }
        if (!canFitEvent(event)) return;
        if (!getEventTtsText(event)) return;

        rendering = true;
        event.speed = 1;

        if (!event.audioFileDuration || eventNeedsRender(event)) {
            await renderSingleEvent(event, { autoTrim: false });
        }

        event.speed = computeResidualSpeed(event);

        insertHistoryAction({
            name: "auto rate",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.event_ad_auto_rate_failed_toast({ message: err.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        rendering = false;
    }
}

function insertEmotionTag(changeEvent) {
    const select = changeEvent.currentTarget;
    const tag = select.value;
    select.value = "";
    if (!tag || isLocked || !window.quillEditor) return;

    try {
        // getSelection(true) refocuses the editor and restores the caret the select took focus from.
        const range = window.quillEditor.getSelection(true);
        if (!range) return;
        if (range.length > 0) {
            window.quillEditor.deleteText(range.index, range.length);
        }
        const needsSpaceAfter = window.quillEditor.getText(range.index, 1).trim().length > 0;
        const inserted = `[${tag}]${needsSpaceAfter ? " " : ""}`;
        window.quillEditor.insertText(range.index, inserted, "user");
        window.quillEditor.setSelection(range.index + inserted.length, 0);

        insertHistoryAction({
            name: "update text",
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    } catch (err) {
        console.log(err, err.message);
    }
}

function recordAudio() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    eventGroupState[projectState.selected].selected = [eventIndex];
    showModal("audioRecorder");
}
</script>

<audio
    {@attach attachPreviewAudioPlayer}
    class="d-none"
    preload="none"
    onended={handlePreviewAudioEnded}
    onerror={handlePreviewAudioError}>
</audio>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<form
    id="Event{eventIndex}"
    aria-label={m.event_audio_description_aria_label({eventIndex})}
    onclick={(clickEvent) => selectEvent(clickEvent, eventIndex)}
    class="rounded-0 {compact ? 'p-2' : 'p-3'} eventItem {eventGroupState[projectState.selected] &&
    eventGroupState[projectState.selected].selected.indexOf(eventIndex) > -1
        ? 'border border-warning bg-warning bg-opacity-25'
        : ''}">
    {#if compact}
        <div class="d-flex justify-content-between align-items-center mb-1">
            <div class="d-flex align-items-center gap-2">
                <p class="text-dark mb-0 fw-bold" title={m.event_id_title()}>
                    {eventIndex + 1}
                </p>
                {#if event}
                    <EventTrackSelect bind:event disabled={isLocked}></EventTrackSelect>
                {/if}
                {#if rendering}
                    <Circle size="20" color="#1eb4b2" unit="px" duration="1s"></Circle>
                {/if}
            </div>
            <div class="btn-group flex-shrink-0 ms-auto" role="group">
                <RealTimeErrors {errorMsg}></RealTimeErrors>
                {#if event}
                    <ForcedSubtitleEvent bind:event disabled={isLocked}></ForcedSubtitleEvent>
                    <EventApproval bind:event></EventApproval>
                    <EventNotes bind:event></EventNotes>
                {/if}
            </div>
        </div>
        <div class="row g-2">
            <div class="col-4">
                <div class="input-group input-group-sm mb-1">
                    <TcInput bind:event tcType="start"></TcInput>
                </div>
                <div class="input-group input-group-sm mb-2">
                    <TcInput bind:event tcType="end"></TcInput>
                </div>
                <EventMetrics
                    {wpm}
                    {cps}
                    {duration}
                    {totalWords}
                    {lineInfo}
                    {totalChars}
                    eventGroupSettings={{
                        maxChars: eventGroupState[projectState.selected].maxChars,
                        maxLines: eventGroupState[projectState.selected].maxLines,
                        maxCps: eventGroupState[projectState.selected].maxCps,
                        maxWpm: eventGroupState[projectState.selected].maxWpm,
                        minDuration:
                            eventGroupState[projectState.selected].minDuration,
                        maxDuration:
                            eventGroupState[projectState.selected].maxDuration,
                    }}></EventMetrics>
                <SliderInput
                    bind:event
                    property="speed"
                    label={m.event_ad_speed_label()}
                    minValue={voiceUtils.SPEED_LIMITS.min}
                    maxValue={voiceUtils.SPEED_LIMITS.max}
                    stepValue="0.01"
                    unit="x"
                    clearsAudio={false}
                    disabled={isLocked}></SliderInput>
                <div
                    class="btn-group btn-group-sm w-100 shadow-sm mt-1"
                    role="group">
                    <!-- Record Button -->
                    {#if event?.voice?.name === "Manual Record"}
                        <button
                            id="recordBtn-{eventIndex}"
                            type="button"
                            class="btn btn-light {needsRender ? 'text-warning' : 'text-dark'}"
                            disabled={rendering || isLocked}
                            onclick={() => recordAudio()}
                            title={m.event_ad_record_voiceover_title()}
                            aria-label={m.event_ad_record_voiceover_aria_label()}>
                            <i class="bi bi-mic-fill"></i>
                        </button>
                    {:else if (environment.online || environment.adLicense) && !isWebSpeechVoice}
                        <button
                            id="renderBtn-{eventIndex}"
                            type="button"
                            class="btn {needsRender
                                ? 'btn-warning text-white glowing-button'
                                : 'btn-light text-dark'}"
                            disabled={rendering || isLocked}
                            onclick={() => renderAudio()}
                            title={m.event_ad_render_tts_title()}
                            aria-label={m.event_ad_render_tts_aria_label()}>
                            <i class="bi bi-bootstrap-reboot"></i>
                        </button>
                    {/if}
                    <!-- Preview Button -->
                    <button
                        id="previewBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light text-dark"
                        disabled={(rendering && !previewAudioPlaying) ||
                            !canPreviewAudio}
                        onclick={stopPropagation(preventDefault(previewAudio))}
                        title={m.event_ad_preview_audio_title()}
                        aria-label={m.event_ad_preview_audio_aria_label()}>
                        {#if previewAudioPlaying}
                            <i class="bi bi-stop-fill"></i>
                        {:else}
                            <i class="bi bi-play-fill"></i>
                        {/if}
                    </button>
                    <!-- Trim Button -->
                    {#if environment.online || environment.adLicense}
                        <button
                            id="trimBtn-{eventIndex}"
                            type="button"
                            title={m.event_ad_trim_duration_title()}
                            aria-label={m.event_ad_trim_duration_aria_label()}
                            class="btn btn-light text-dark"
                            disabled={rendering ||
                                isLocked ||
                                isNaN(event.start) ||
                                !event.audioFile}
                            onclick={trimToDuration}>
                            <i class="bi bi-scissors"></i>
                        </button>
                    {/if}
                </div>
                {#if needsRender}
                    <span
                        class="badge bg-warning text-dark p-1 mt-1"
                        title={m.event_ad_render_required_title()}
                        ><i class="bi bi-exclamation-lg"></i> {m.event_ad_render_required_badge()}</span>
                {/if}
                {#if audibleDuration && duration < audibleDuration - 0.5}
                    <span
                        class="badge bg-danger p-1 mt-1"
                        title={m.event_ad_duration_warning_title()}
                        ><i class="bi bi-exclamation-lg"></i> {m.event_ad_duration_badge()}</span>
                {/if}
            </div>
            <div class="col">
                {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
                    <QuillEditor bind:event bind:eventIndex></QuillEditor>
                {:else}
                    <TranscriptEditorPreview bind:event bind:eventIndex></TranscriptEditorPreview>
                {/if}
            </div>
        </div>
    {:else}
    <div class="row">
        <!-- Event Metrics -->
        <div class="col-2">
            <div class="btn-group float-end" role="group">
                <RealTimeErrors {errorMsg}></RealTimeErrors>
                {#if event}
                    <ForcedSubtitleEvent bind:event disabled={isLocked}></ForcedSubtitleEvent>
                    <EventApproval bind:event></EventApproval>
                    <EventNotes bind:event></EventNotes>
                {/if}
            </div>

            <div class="d-flex align-items-center gap-2 mb-1">
                <p class="text-dark mb-0 fw-bold" title={m.event_id_title()}>
                    {eventIndex + 1}
                </p>
                {#if event}
                    <EventTrackSelect bind:event disabled={isLocked}></EventTrackSelect>
                {/if}
            </div>
            <p
                class="text-truncate text-custom-small text-dark fst-italic m-0"
                title={event
                    .voice.name}>
                {event.voice
                    .name}
            </p>
            {#if isElevenLabsVoice}
                <!-- ElevenLabs model is stored in speakingStyle; legacy "default" shows the model it resolves to -->
                <select
                    id="elevenLabsModelSelect-{eventIndex}"
                    class="text-custom-medium speakerStyleSelect mb-2 w-100"
                    aria-label={m.event_ad_elevenlabs_model_label()}
                    title={m.event_ad_elevenlabs_model_label()}
                    value={elevenLabsModel}
                    disabled={isLocked}
                    onchange={(e) => {
                        event.speakingStyle = e.currentTarget.value;
                        event.rendered = false;
                    }}>
                    {#each voiceUtils.ELEVENLABS_MODELS as model (model.id)}
                        <option value={model.id}>{model.label}</option>
                    {/each}
                </select>
            {/if}
            {#if emotionTagsSupported && eventGroupState[projectState.selected]?.selected[0] === eventIndex}
                <select
                    id="emotionTagSelect-{eventIndex}"
                    class="text-custom-medium speakerStyleSelect mb-2 w-100"
                    aria-label={m.event_ad_insert_emotion_tag_label()}
                    title={m.event_ad_insert_emotion_tag_title()}
                    value=""
                    disabled={isLocked}
                    onchange={insertEmotionTag}>
                    <option value="" disabled>{m.event_ad_insert_emotion_tag_label()}</option>
                    {#each voiceUtils.EMOTION_TAGS as tag (tag)}
                        <option value={tag}>[{tag}]</option>
                    {/each}
                </select>
            {/if}

            <EventMetrics
                {wpm}
                {cps}
                {duration}
                {totalWords}
                {lineInfo}
                {totalChars}
                eventGroupSettings={{
                    maxChars: eventGroupState[projectState.selected].maxChars,
                    maxLines: eventGroupState[projectState.selected].maxLines,
                    maxCps: eventGroupState[projectState.selected].maxCps,
                    maxWpm: eventGroupState[projectState.selected].maxWpm,
                    minDuration:
                        eventGroupState[projectState.selected].minDuration,
                    maxDuration:
                        eventGroupState[projectState.selected].maxDuration,
                }}></EventMetrics>

            {#if rendering}
                <Circle size="30" color="#1eb4b2" unit="px" duration="1s"
                ></Circle>
            {/if}
        </div>
        <!-- Event Timecode -->
        <div class="col-auto">
            <div class="input-group input-group-sm mb-1">
                <TcInput bind:event tcType="start"></TcInput>
            </div>
            <div class="input-group input-group-sm mb-2">
                <TcInput bind:event tcType="end"></TcInput>
            </div>
            {#if event?.voice?.provider === WEB_SPEECH_PROVIDER}
                <SliderInput
                    bind:event
                    property="pitch"
                    label={m.event_ad_pitch_label()}
                    minValue="0"
                    maxValue="2"
                    stepValue="0.01"
                    unit="x"
                    disabled={isLocked}></SliderInput>
            {:else if event?.voice?.provider === "ElevenLabs"}
                <SliderInput
                    bind:event
                    property="voiceStability"
                    label="Stability"
                    minValue="30"
                    maxValue="100"
                    stepValue="1"
                    unit="%"
                    disabled={isLocked}></SliderInput>
                <SliderInput
                    bind:event
                    property="voiceSimilarity"
                    label="Similarity"
                    minValue="30"
                    maxValue="100"
                    stepValue="1"
                    unit="%"
                    disabled={isLocked}></SliderInput>
            {/if}
            <SliderInput
                bind:event
                property="speed"
                label={m.event_ad_speed_label()}
                minValue={voiceUtils.SPEED_LIMITS.min}
                maxValue={voiceUtils.SPEED_LIMITS.max}
                stepValue="0.01"
                unit="x"
                clearsAudio={false}
                disabled={isLocked}></SliderInput>
            <div
                class="btn-group btn-group-sm w-100 shadow-sm"
                role="group">
                <!-- Record Button -->
                {#if event?.voice?.name === "Manual Record"}
                    <button
                        id="recordBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light {needsRender ? 'text-warning' : 'text-dark'}"
                        disabled={rendering || isLocked}
                        onclick={() => recordAudio()}
                        title={m.event_ad_record_voiceover_title()}
                        aria-label={m.event_ad_record_voiceover_aria_label()}>
                        <i class="bi bi-mic-fill"></i>
                    </button>
                {:else if (environment.online || environment.adLicense) && !isWebSpeechVoice}
                    <button
                        id="renderBtn-{eventIndex}"
                        type="button"
                        class="btn {needsRender
                            ? 'btn-warning text-white glowing-button'
                            : 'btn-light text-dark'}"
                        disabled={rendering || isLocked}
                        onclick={() => renderAudio()}
                        title={m.event_ad_render_tts_title()}
                        aria-label={m.event_ad_render_tts_aria_label()}>
                        <i class="bi bi-bootstrap-reboot"></i>
                    </button>
                    <button
                        id="autoRateBtn-{eventIndex}"
                        type="button"
                        class="btn btn-light text-dark"
                        disabled={rendering ||
                            isLocked ||
                            isNaN(event.start) ||
                            isNaN(event.end) ||
                            event.end <= event.start ||
                            !plainText.trim()}
                        onclick={stopPropagation(preventDefault(autoRate))}
                        title={m.event_ad_auto_rate_title()}
                        aria-label={m.event_ad_auto_rate_aria_label()}>
                        <i class="bi bi-speedometer2"></i>
                    </button>
                {/if}
                <!-- Preview Button -->
                <button
                    id="previewBtn-{eventIndex}"
                    type="button"
                    class="btn btn-light text-dark"
                    disabled={(rendering && !previewAudioPlaying) ||
                        !canPreviewAudio}
                    onclick={stopPropagation(preventDefault(previewAudio))}
                    title={m.event_ad_preview_audio_title()}
                    aria-label={m.event_ad_preview_audio_aria_label()}>
                    {#if previewAudioPlaying}
                        <i class="bi bi-stop-fill"></i>
                    {:else}
                        <i class="bi bi-play-fill"></i>
                    {/if}
                </button>
                <!-- Trim Button -->
                {#if environment.online || environment.adLicense}
                    <button
                        id="trimBtn-{eventIndex}"
                        type="button"
                        title={m.event_ad_trim_duration_title()}
                        aria-label={m.event_ad_trim_duration_aria_label()}
                        class="btn btn-light text-dark"
                        disabled={rendering ||
                            isLocked ||
                            isNaN(
                                event.start,
                            ) ||
                            !event.audioFile}
                        onclick={trimToDuration}>
                        <i class="bi bi-scissors"></i>
                    </button>
                {/if}
                <!-- Open AI -->
                {#if environment.online}
                    <div
                        title={m.event_ad_ai_tools_title()}
                        class="btn-group btn-group-sm"
                        role="group">
                        <button
                            bind:this={aiToolsBtnEl}
                            id="aiToolsBtn-{eventIndex}"
                            type="button"
                            class="btn btn-light dropdown-toggle {showAiTools
                                ? 'show'
                                : ''}"
                            disabled={isLocked}
                            onclick={() => toggleAiToolsMenu()}
                            aria-haspopup="true"
                            aria-expanded={showAiTools}
                            aria-label={m.event_ad_ai_tools_menu_aria_label()}>
                            <i class="bi bi-cpu"></i>
                        </button>
                        {#if showAiTools}
                            <ul
                                {@attach portalAiMenu}
                                class="dropdown-menu show ai-tools-portal-menu"
                                style="position: fixed; top: {aiMenuPos.top}px; left: {aiMenuPos.left}px; margin: 0; z-index: 2000;"
                                use:clickoutside
                                onclickoutside={closeAiToolsMenu}>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase("shorten")}
                                        >{m.event_ad_rephrase_shorten()}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase("expand")}
                                        >{m.event_ad_rephrase_expand()}</a>
                                </li>
                                <li><hr class="dropdown-divider" /></li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(150)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 150 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(175)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 175 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(200)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 200 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(225)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 225 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(250)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 250 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(275)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 275 })}</a>
                                </li>
                                <li>
                                    <a
                                        class="dropdown-item"
                                        href="#!/"
                                        onclick={() => aiRephrase(300)}
                                        >{m.event_ad_rephrase_to_wpm({ wpm: 300 })}</a>
                                </li>
                            </ul>
                        {/if}
                    </div>
                {/if}
            </div>
            {#if needsRender}
                <span
                    class="badge bg-warning text-dark p-1 mt-2"
                    title={m.event_ad_render_required_title()}
                    ><i class="bi bi-exclamation-lg"></i> {m.event_ad_render_required_badge()}</span>
            {/if}
            {#if audibleDuration && duration < audibleDuration - 0.5}
                <span
                    class="badge bg-danger p-1 mt-2"
                    title={m.event_ad_duration_warning_title()}
                    ><i class="bi bi-exclamation-lg"></i> {m.event_ad_duration_badge()}</span>
            {/if}
        </div>
        <!-- Event Text -->
        <div class="col">
            {#if isDubbing}
                <div class="row">
                    <div class="col-6">
                        {#if isSelected && allowOriginalEdit && !isLocked}
                            <QuillEditor bind:event bind:eventIndex textProperty="original" autoFocus={false}></QuillEditor>
                        {:else}
                            <OriginalTextPreview
                                text={event.original}
                                fontFamily={eventGroupState[projectState.selected].originalFontFamily}
                                fontSize={eventGroupState[projectState.selected].fontSize}></OriginalTextPreview>
                        {/if}
                    </div>
                    <div class="col-6">
                        {#if isSelected}
                            <QuillEditor bind:event bind:eventIndex></QuillEditor>
                        {:else}
                            <TranscriptEditorPreview bind:event bind:eventIndex></TranscriptEditorPreview>
                        {/if}
                    </div>
                </div>
            {:else if isSelected}
                <QuillEditor bind:event bind:eventIndex></QuillEditor>
            {:else}
                <TranscriptEditorPreview bind:event bind:eventIndex></TranscriptEditorPreview>
            {/if}
            <div class="row">
                <EventSpeakers bind:event disabled={isLocked}></EventSpeakers>
                <EventRecipients bind:event disabled={isLocked}></EventRecipients>
                <EventTags bind:event disabled={isLocked}></EventTags>
            </div>
        </div>
    </div>
    {/if}
    {#if eventGroupState[projectState.selected]?.selected[0] === eventIndex}
        <div class="event-action-buttons">
            <button
                aria-label={m.event_insert_button_aria_label()}
                type="button"
                class="btn btn-primary btn-sm newEventButton"
                title={m.event_insert_button_title()}
                disabled={isLocked}
                onclick={insertEvent}><i class="bi bi-plus-lg"></i></button>
            <button
                aria-label={m.event_delete_button_aria_label()}
                type="button"
                class="btn btn-danger btn-sm deleteEventButton"
                title={m.event_delete_button_title()}
                disabled={isLocked}
                onclick={() => deleteEvent()}>
                <i class="bi bi-trash"></i>
            </button>
        </div>
    {/if}
</form>

<style lang="css">
.event-action-buttons {
    position: absolute;
    bottom: -10px;
    left: 5%;
    transform: translateX(-0%);
    z-index: 1059;
    display: flex;
    gap: 5px;
}

.newEventButton, .deleteEventButton {
    box-shadow: 0.3rem 0.3rem 0.3rem rgb(0 0 0 / 50%);
    color: white;
}

.eventItem {
    min-height: 229px !important;
    position: relative;
}

.speakerStyleSelect {
    padding: 0;
    background: none;
    border: none;
    border-bottom: 1px solid;
}

select > option {
    color: var(--bs-light);
}

.glowing-button {
    animation: glowing 2000ms infinite;
    box-shadow: 0 0 3px #0fbc8c;
}

@keyframes glowing {
    0% {
        background-color: #0fbc8c;
        box-shadow: 0 0 3px #0fbc8c;
    }
    50% {
        background-color: #11dda3;
        box-shadow: 0 0 10px #11dda3;
    }
    100% {
        background-color: #0de9aa;
        box-shadow: 0 0 3px #0de9aa;
    }
}
</style>
